import { useAuth } from "@clerk/tanstack-react-start";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { ErrorComponent, createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { addMinutes } from "date-fns";
import { Calendar, Clock, Loader2, Monitor } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { MarkdownContent } from "~/components/markdown-content";
import { SummitRegistration } from "~/components/summit/summit-registration";
import { shouldShowSummitSpecial } from "~/components/summit/summit-special-button";
import { AuthorCard } from "~/components/ui/author-card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { WaveBackground } from "~/components/ui/wave-background";
import {
  getSessionDataOptions,
  getSessionRegistrationStateOptions,
  registerForSession,
  unregisterFromSession,
} from "~/lib/functions/summit";
import { toMajorUnits } from "~/lib/shared/currency";
import { createBrandMeta } from "~/lib/shared/meta";
import {
  SUMMIT_SPOT_SECURED_REDIRECT_URL,
  SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
} from "~/lib/summit";
import type { SummitSession } from "~/lib/summit";
import { formatSummitSessionFormat } from "~/lib/summit";
import { formatSummitTimeRangeForUser } from "~/lib/summit";

/** Minutes before session start when join button becomes visible */
const JOIN_VISIBLE_BEFORE_START_MINUTES = 15;

type SessionPhase = "upcoming" | "joinable" | "live" | "ended";

function useSessionPhase(startTimeUtc: Date, endTimeUtc: Date): SessionPhase {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    const joinableAt = addMinutes(
      startTimeUtc,
      -JOIN_VISIBLE_BEFORE_START_MINUTES,
    );
    if (now >= endTimeUtc) return "ended";
    if (now >= startTimeUtc) return "live";
    if (now >= joinableAt) return "joinable";
    return "upcoming";
  }, [now, startTimeUtc, endTimeUtc]);
}

export const Route = createFileRoute(
  "/_public/summit/$summitId/session/$sessionId",
)({
  loader: ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      getSessionDataOptions(params.sessionId),
    );
  },
  head: ({ loaderData, params }) => {
    const { session, summit } = loaderData || {};

    if (!session) {
      return createBrandMeta({
        title: "Summit Session",
        description:
          "Join us for an exclusive summit session with industry experts.",
        ogType: "website",
      });
    }

    // Generate description combining session and speaker info
    const speakerInfo = session.speaker.title
      ? `${session.speaker.name}, ${session.speaker.title}`
      : session.speaker.name;

    const description = session.fullDescription || session.description;
    const enhancedDescription = `${description} Join ${speakerInfo} for this ${formatSummitSessionFormat(session.sessionFormat)} session.`;

    // Generate keywords from session data
    const keywords = [
      "summit",
      "session",
      "coaching",
      session.sessionFormat || "presentation",
      session.speaker.name,
      session.speaker.title || "",
      ...session.topics,
    ]
      .filter(Boolean)
      .filter((keyword): keyword is string => typeof keyword === "string");

    // Session URL
    const sessionUrl = `https://dreamcoachmatch.com/summit/${params.summitId}/session/${params.sessionId}`;

    // Use custom session social image
    const ogImage = `https://dreamcoachmatch.com/summit/${params.summitId}/session/${params.sessionId}/social-image.svg`;

    const metaTags = createBrandMeta({
      title: session.title,
      description: enhancedDescription,
      ogTitle: `${session.title} | ${summit?.title || "Summit"}`,
      ogDescription: description,
      ogImage,
      ogType: "website",
      ogUrl: sessionUrl,
      twitterCard: "summary_large_image",
      twitterTitle: session.title,
      twitterDescription: description,
      twitterImage: ogImage,
      canonical: sessionUrl,
      keywords,
      author: session.speaker.name,
    });

    // Create Event structured data
    const endTimeUtc = new Date(
      session.startTimeUtc.getTime() + session.durationMinutes * 60000,
    );

    const eventStructuredData = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: session.title,
      description: description,
      startDate: session.startTimeUtc.toISOString(),
      endDate: endTimeUtc.toISOString(),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      location: {
        "@type": "VirtualLocation",
        url: sessionUrl,
      },
      performer: {
        "@type": "Person",
        name: session.speaker.name,
        jobTitle: session.speaker.title,
        image: session.speaker.avatarUrl,
        ...(session.speaker.bio && { description: session.speaker.bio }),
        ...(session.speaker.linkedInUrl && {
          sameAs: [
            session.speaker.linkedInUrl,
            session.speaker.twitterUrl,
            session.speaker.websiteUrl,
          ].filter(Boolean),
        }),
      },
      organizer: {
        "@type": "Organization",
        name: "Dream Coach Match",
        url: "https://dreamcoachmatch.com",
        address: {
          "@type": "PostalAddress",
          streetAddress:
            "c/o The Corporation Trust Company, 1209 Orange Street",
          addressLocality: "Wilmington",
          addressRegion: "DE",
          postalCode: "19801",
          addressCountry: "US",
        },
        telephone: "+1-425-441-3867",
      },
      ...(session.topics.length > 0 && {
        about: session.topics.map((topic) => ({
          "@type": "Thing",
          name: topic,
        })),
      }),
    };

    return {
      ...metaTags,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(eventStructuredData),
        },
      ],
    };
  },
  component: SessionPage,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
});

interface SessionRegistrationStepProps {
  sessionId: string;
  sessionTitle: string;
  sessionFormat?: SummitSession["sessionFormat"];
  sessionPhase: SessionPhase;
}

function SessionPage() {
  const { sessionId } = Route.useParams();
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(getSessionDataOptions(sessionId));
  const { session } = data;
  const sessionFormatLabel = formatSummitSessionFormat(session.sessionFormat);

  const registrationQuery = useQuery({
    ...getSessionRegistrationStateOptions(sessionId),
    enabled: isSignedIn,
  });

  const unregisterMutation = useMutation({
    mutationFn: unregisterFromSession,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["session", sessionId, "registration-state"],
      });
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
    },
  });

  const registration = registrationQuery.data?.registration;
  const meetingUrl = registrationQuery.data?.session.meetingUrl ?? null;
  const isRegistered = registration?.session.isRegistered ?? false;
  const specialIsVisible = shouldShowSummitSpecial(
    session.summitOffer,
    session.sessionStartUtc,
  );

  const startTimeUtc = useMemo(
    () =>
      session.startTimeUtc instanceof Date
        ? session.startTimeUtc
        : new Date(session.startTimeUtc),
    [session.startTimeUtc],
  );

  const endTimeUtc = useMemo(
    () => addMinutes(startTimeUtc, session.durationMinutes),
    [startTimeUtc, session.durationMinutes],
  );

  const timeDisplay = useMemo(
    () => formatSummitTimeRangeForUser(startTimeUtc, endTimeUtc),
    [startTimeUtc, endTimeUtc],
  );

  const sessionPhase = useSessionPhase(startTimeUtc, endTimeUtc);
  const isJoinWindow = sessionPhase === "joinable" || sessionPhase === "live";
  const isEnded = sessionPhase === "ended";

  const handleCancelRegistration = () => {
    unregisterMutation.mutate({ data: { sessionId } });
  };

  return (
    <div className="min-h-dvh bg-gray-50">
      {/* Hero Section */}
      <div className="relative">
        <WaveBackground />

        {/* Overlay for better contrast */}
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 mx-auto max-w-4xl px-6 py-12">
          {/* Session metadata */}
          <div className="z-10 mb-6 flex flex-wrap items-center gap-3">
            {session.sessionFormat && (
              <Badge variant="secondary" className="gap-1.5">
                <Monitor className="h-3 w-3" />
                {sessionFormatLabel}
              </Badge>
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-sm text-white/80">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {timeDisplay.startTime} - {timeDisplay.endTime}
                </span>
              </div>
              {timeDisplay.summitTimeRange && (
                <div className="pl-5 text-xs text-white/60">
                  {timeDisplay.summitTimeRange}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {timeDisplay.date
                  ? timeDisplay.date
                  : format(session.date, "EEEE, MMMM d, yyyy")}
              </span>
            </div>
          </div>

          {/* Session title */}
          <div className="mt-12 mb-4 flex flex-row items-center justify-between gap-4">
            <h1 className="max-w-xl font-heading text-4xl tracking-tight text-balance text-white md:text-5xl lg:text-6xl">
              {session.title}
            </h1>
            {!isEnded && (
              <div className="flex flex-col items-end gap-2">
                {isRegistered ? (
                  <div className="flex flex-col items-end gap-2">
                    {isJoinWindow && meetingUrl && (
                      <Button
                        variant="primary"
                        className="shadow-lg"
                        onClick={() => window.open(meetingUrl, "_blank")}
                      >
                        Join Live Session
                      </Button>
                    )}
                    {!specialIsVisible && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/80 hover:bg-white/10 hover:text-white"
                        onClick={handleCancelRegistration}
                        loading={unregisterMutation.isPending}
                      >
                        Cancel Registration
                      </Button>
                    )}
                  </div>
                ) : (
                  <a href="#register">
                    <Button variant="primary" className="shadow-lg">
                      Register
                    </Button>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Speaker section */}
          <AuthorCard
            author={session.speaker}
            variant="medium"
            showBio={true}
            showSocialLinks={true}
            titleLevel="h2"
            onDark={true}
          />
        </div>
      </div>

      {/* Content Section */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="grid gap-12 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="space-y-8">
              {/* Session description */}
              <div>
                <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                  About This Session
                </h3>
                {session.fullDescription && (
                  <MarkdownContent
                    content={session.fullDescription}
                    className="prose"
                  />
                )}
              </div>

              {/* Topics covered */}
              {session.topics.length > 0 && (
                <div>
                  <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                    Topics Covered
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {session.topics.map((topic) => (
                      <Badge key={topic} variant="secondary" size="lg">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Card>
                <CardContent className="space-y-10">
                  <a id="register" className="anchor -mt-12" />
                  <section>
                    <div className="flex items-center gap-3 text-xs font-semibold tracking-[0.24em] text-purple-600 uppercase">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-base text-purple-700">
                        1
                      </span>
                      Step 1 · Summit registration
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                      Save your complimentary pass to unlock every keynote,
                      panel, and networking moment.
                    </p>
                    <div className="mt-6">
                      <SummitRegistration
                        redirectUrl={SUMMIT_SPOT_SECURED_REDIRECT_URL}
                      />
                    </div>
                  </section>

                  <Separator />

                  <SessionRegistrationStep
                    sessionId={session.id}
                    sessionTitle={session.title}
                    sessionFormat={session.sessionFormat}
                    sessionPhase={sessionPhase}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sidebar - Registration CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Special Offer CTA */}
              {specialIsVisible && session.summitOffer && (
                <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50">
                  <CardContent>
                    {session.summitOffer.headline && (
                      <h3 className="mb-4 text-lg font-semibold text-gray-900">
                        {session.summitOffer.headline}
                      </h3>
                    )}
                    {session.summitOffer.price &&
                      session.summitOffer.currency && (
                        <div className="mb-4">
                          <span className="text-3xl font-bold text-gray-900">
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: session.summitOffer.currency,
                            }).format(
                              toMajorUnits(
                                session.summitOffer.price,
                                session.summitOffer.currency,
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    <Link
                      to="/$username/offer/$offerId"
                      params={{
                        username: session.summitOffer.coachUsername,
                        offerId: session.summitOffer.id,
                      }}
                    >
                      <Button
                        variant="primary"
                        className="w-full shadow-lg"
                        size="default"
                      >
                        Open Special Offer
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* Additional session info */}
              <Card>
                <CardContent>
                  <h4 className="mb-4 font-semibold text-gray-900">
                    Session Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Format</span>
                      <span className="font-medium">{sessionFormatLabel}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Duration</span>
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-medium">
                          {timeDisplay.startTime} - {timeDisplay.endTime}
                        </span>
                        {timeDisplay.summitTimeRange && (
                          <span className="text-xs text-gray-500">
                            {timeDisplay.summitTimeRange}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Date</span>
                      <span className="font-medium">
                        {format(session.date, "MMM d, yyyy")}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionRegistrationStep({
  sessionId,
  sessionTitle,
  sessionFormat,
  sessionPhase,
}: SessionRegistrationStepProps) {
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const registrationQuery = useQuery({
    ...getSessionRegistrationStateOptions(sessionId),
    enabled: isSignedIn,
  });

  const registerMutation = useMutation({
    mutationFn: registerForSession,
    onMutate: () => setActionError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["session", sessionId, "registration-state"],
      });
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
    },
    onError: (error) => {
      setActionError(
        error instanceof Error
          ? error.message
          : "We couldn't save your seat. Please try again.",
      );
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: unregisterFromSession,
    onMutate: () => setActionError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["session", sessionId, "registration-state"],
      });
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
    },
    onError: (error) => {
      setActionError(
        error instanceof Error
          ? error.message
          : "We couldn't update your registration right now. Please try again.",
      );
    },
  });

  const registration = registrationQuery.data?.registration;
  const meetingUrl = registrationQuery.data?.session.meetingUrl ?? null;
  const isWorkshop = registration?.isWorkshop ?? false;
  const limitReached = registration?.limitReached ?? false;
  const registrationLimit =
    registration?.workshopRegistrationLimit ??
    SUMMIT_WORKSHOP_REGISTRATION_LIMIT;
  const workshopRegistrationsCount =
    registration?.workshopRegistrationsCount ?? 0;
  const sessionFormatLabel = formatSummitSessionFormat(sessionFormat);

  const isJoinWindow = sessionPhase === "joinable" || sessionPhase === "live";
  const isEnded = sessionPhase === "ended";

  const handleRegister = () => registerMutation.mutate({ data: { sessionId } });
  const handleCancelRegistration = () =>
    unregisterMutation.mutate({ data: { sessionId } });

  const content: ReactNode = (() => {
    // Session has ended - no registration actions available
    if (isEnded) {
      return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          <p>This session has ended.</p>
        </div>
      );
    }

    if (!isSignedIn) {
      return <NotSignedInView sessionFormatLabel={sessionFormatLabel} />;
    }

    if (registrationQuery.isLoading || registrationQuery.isFetching) {
      return <LoadingRegistrationView />;
    }

    if (!registration) {
      return <RegistrationUnavailableView />;
    }

    if (!registration.summit.isRegistered) {
      return <SummitRegistrationRequiredView />;
    }

    if (registration.session.isRegistered) {
      return (
        <AlreadyRegisteredView
          isWorkshop={isWorkshop}
          meetingUrl={meetingUrl}
          onCancel={handleCancelRegistration}
          registrationLimit={registrationLimit}
          sessionTitle={sessionTitle}
          workshopRegistrationsCount={workshopRegistrationsCount}
          isCancelling={unregisterMutation.isPending}
          isJoinWindow={isJoinWindow}
        />
      );
    }

    return (
      <RegistrationCallToActionView
        isRegistering={registerMutation.isPending}
        isWorkshop={isWorkshop}
        limitReached={limitReached}
        onRegister={handleRegister}
        registrationLimit={registrationLimit}
        sessionFormatLabel={sessionFormatLabel}
        sessionTitle={sessionTitle}
        workshopRegistrationsCount={workshopRegistrationsCount}
        isJoinWindow={isJoinWindow}
        meetingUrl={meetingUrl}
      />
    );
  })();

  return (
    <section>
      <div className="flex items-center gap-3 text-xs font-semibold tracking-[0.24em] text-purple-600 uppercase">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-base text-purple-700">
          2
        </span>
        Step 2 · Claim your seat
      </div>
      <p className="mt-3 text-sm text-gray-600">
        Choose your spot for the {sessionFormatLabel.toLowerCase()} and we'll
        send the details straight to your inbox.
      </p>
      <div className="mt-6 space-y-4">
        {content}
        {actionError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {actionError}
          </div>
        )}
      </div>
    </section>
  );
}

interface NotSignedInViewProps {
  sessionFormatLabel: string;
}

function NotSignedInView({ sessionFormatLabel }: NotSignedInViewProps) {
  return (
    <div className="rounded-lg border border-dashed border-purple-200 bg-white p-6 text-sm text-gray-600">
      <p>
        Sign in or complete Step 1 above to reserve your seat for this{" "}
        {sessionFormatLabel.toLowerCase()}.
      </p>
      <p className="mt-2 text-xs text-gray-500">
        Already registered for the summit? Log in to manage your sessions.
      </p>
    </div>
  );
}

function LoadingRegistrationView() {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-purple-100 bg-white p-6 text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
      Checking your session access...
    </div>
  );
}

function RegistrationUnavailableView() {
  return (
    <div className="rounded-lg border border-dashed border-purple-200 bg-white p-6 text-sm text-gray-600">
      <p>
        We couldn't confirm your session access. Please refresh and try again.
      </p>
    </div>
  );
}

function SummitRegistrationRequiredView() {
  return (
    <div className="rounded-lg border border-dashed border-purple-200 bg-white p-6 text-sm text-gray-600">
      <p>Register for the summit first to unlock session registration.</p>
    </div>
  );
}

interface AlreadyRegisteredViewProps {
  sessionTitle: string;
  meetingUrl: string | null;
  isWorkshop: boolean;
  workshopRegistrationsCount: number;
  registrationLimit: number;
  onCancel: () => void;
  isCancelling: boolean;
  isJoinWindow: boolean;
}

function AlreadyRegisteredView({
  sessionTitle,
  meetingUrl,
  isWorkshop,
  workshopRegistrationsCount,
  registrationLimit,
  onCancel,
  isCancelling,
  isJoinWindow,
}: AlreadyRegisteredViewProps) {
  return (
    <div className="space-y-4 rounded-lg border border-green-200 bg-green-50 p-6 text-sm text-green-900">
      <div>
        <p className="font-semibold">You're all set for {sessionTitle}.</p>
        {isWorkshop && (
          <p className="mt-1 text-xs text-green-800">
            You've reserved {workshopRegistrationsCount}/{registrationLimit}{" "}
            workshop spots.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {isJoinWindow && meetingUrl && (
          <Button
            variant="default"
            className="w-full sm:w-auto"
            onClick={() => window.open(meetingUrl, "_blank")}
          >
            Join Live Session
          </Button>
        )}
        <Button
          className="w-full sm:w-auto"
          loading={isCancelling}
          onClick={onCancel}
        >
          Cancel Registration
        </Button>
      </div>
    </div>
  );
}

interface RegistrationCallToActionViewProps {
  sessionTitle: string;
  sessionFormatLabel: string;
  isWorkshop: boolean;
  registrationLimit: number;
  workshopRegistrationsCount: number;
  limitReached: boolean;
  onRegister: () => void;
  isRegistering: boolean;
  isJoinWindow: boolean;
  meetingUrl: string | null;
}

function RegistrationCallToActionView({
  sessionTitle,
  sessionFormatLabel,
  isWorkshop,
  registrationLimit,
  workshopRegistrationsCount,
  limitReached,
  onRegister,
  isRegistering,
  isJoinWindow,
  meetingUrl,
}: RegistrationCallToActionViewProps) {
  const helperText = limitReached
    ? `You've already registered for ${workshopRegistrationsCount}/${registrationLimit} workshops. Cancel an existing workshop to free up a spot.`
    : isWorkshop
      ? `You can register for up to ${registrationLimit} workshops.`
      : "You're moments away from confirming your seat.";

  const canRegisterAndJoin =
    isJoinWindow && meetingUrl !== null && !limitReached;

  const handleRegisterAndJoin = async () => {
    onRegister();
    if (meetingUrl) {
      window.open(meetingUrl, "_blank");
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-purple-100 bg-white p-6 text-sm text-gray-700">
      <p>
        Reserve your seat for <strong>{sessionTitle}</strong>.
      </p>
      {canRegisterAndJoin ? (
        <Button
          className="w-full sm:w-auto"
          loading={isRegistering}
          disabled={isRegistering}
          onClick={handleRegisterAndJoin}
        >
          Register & Join
        </Button>
      ) : (
        <Button
          className="w-full sm:w-auto"
          loading={isRegistering}
          disabled={isRegistering || limitReached}
          onClick={onRegister}
        >
          {limitReached
            ? "Workshop limit reached"
            : `Register for this ${sessionFormatLabel.toLowerCase()}`}
        </Button>
      )}
      <p className="text-xs text-gray-500">{helperText}</p>
      {isWorkshop && (
        <p className="text-xs text-gray-500">
          You've registered for {workshopRegistrationsCount}/{registrationLimit}{" "}
          workshops.
        </p>
      )}
    </div>
  );
}
